import { getPurchaseOrdersTemplate } from "@/api/template";
import ActionsPopOverItem from "@/components/ActionsPopOver/ActionsPopOverItem";
import FileDownloader from "@/components/FileDownloader/FileDownloader";
import FileUploader from "@/components/FileUploader/FileUploader";
import HelpButton from "@/components/HelpButton/HelpButton";
import CrossReference from "@/components/links/CrossReference/CrossReference";
import MoreButton from "@/components/MoreButton/MoreButton";
import PageButtonGroup from "@/components/PageButtonGroup/PageButtonGroup";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import StatusTag from "@/components/StatusTag/StatusTag";
import StatusTags from "@/components/StatusTags/StatusTags";
import useTableStateUrl from "@/components/Table/hooks/useTableStateUrl";
import Table, { Column, TableProps } from "@/components/Table/Table";
import TableHeader from "@/components/TableHeader/TableHeader";
import YearSelector from "@/components/YearSelector/YearSelector";
import { IOrder, useOrders, useOrderStatusPerYear } from "@/hooks/useOrders";
import useTrackOnLoad from "@/hooks/useTrackPageview";
import { DeleteOutlined, DownloadOutlined, ExportOutlined, LinkOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { useState } from "react";
import { useDashboardYear } from "../dashboard/hooks/useDateRange";
import { PURCHASE_ORDER_UPLOAD_COLUMNS } from "./constants";
import DeleteOrdersAction from "./DeleteOrdersAction";
import ExportOrdersAction from "./ExportOrdersAction";

const columns: Column<IOrder>[] = [
  {
    label: "Order ID",
    key: "order_id",
    searchable: true,
    minWidth: "140px",
  },
  {
    label: "Order date",
    key: "order_date",
    minWidth: "150px",
    sortable: true,
  },
  {
    label: "Product ID",
    key: "product_external_id",
    minWidth: "160px",
    searchable: true,
    renderer: (record) => (
      <CrossReference to={"/products/" + record.product_id}>
        {record.product_external_id}
      </CrossReference>
    ),
  },
  {
    label: "Product Name",
    key: "product_name",
    minWidth: "180px",
    searchable: true,
    maxWidth: "2fr",
    renderer: (record) => (
      <CrossReference to={"/products/" + record.product_id}>{record.product_name}</CrossReference>
    ),
  },
  {
    label: "Quantity",
    key: "quantity",
    minWidth: "140px",
    sortable: true,
  },
  {
    key: "departure_facility_name",
    label: "Departure facility",
    searchable: true,
    maxWidth: "2fr",
    minWidth: "230px",
    sortable: true,
    renderer: (record) => (
      <CrossReference to={"/facilities/" + record.departure_facility_id}>
        {record.departure_facility_name}
      </CrossReference>
    ),
  },
  {
    key: "arrival_facility_name",
    label: "Arrival facility",
    searchable: true,
    maxWidth: "2fr",
    minWidth: "230px",
    sortable: true,
    renderer: (record) => (
      <CrossReference to={"/facilities/" + record.arrival_facility_id}>
        {record.arrival_facility_name}
      </CrossReference>
    ),
  },
  {
    key: "transport_category",
    label: "Transport category",
    searchable: true,
    maxWidth: "1fr",
    minWidth: "240px",
    sortable: true,
    renderer: (record) => record.transport_category || "-",
  },
  {
    label: "Status",
    key: "status",
    minWidth: "130px",
    filterable: true,
    renderer: (po) => {
      return <StatusTag status={po.status} />;
    },
  },
  {
    label: "Actions",
    key: "actions",
    minWidth: "85px",
    maxWidth: "85px",
    renderer: (record) => {
      return (
        <MoreButton
          buttonProps={{ type: "text" }}
          actions={
            <>
              <ActionsPopOverItem
                to={`/transport/inbound/${record.departure_facility_id}/${record.arrival_facility_id}/${record.transport_category}?search_order_id=${record.order_id}`}
                icon={<LinkOutlined />}
              >
                View transport
              </ActionsPopOverItem>
            </>
          }
        />
      );
    },
  },
];

function PurchaseOrders() {
  useTrackOnLoad("Purchase Orders Viewed");

  const { pagination, filter, search, sort, onSearchFilterSortPaginate, setPage } =
    useTableStateUrl({
      columns,
    });

  const { year } = useDashboardYear();

  const { data, isLoading, isFetching } = useOrders({
    orderType: "purchase",
    pagination,
    filter,
    search,
    sort,
    year,
    enabled: !!year,
  });

  const availableFilters = {
    status: [
      { label: "Complete", value: "complete" },
      { label: "Incomplete", value: "incomplete" },
      { label: "Error", value: "errors" },
    ],
  };

  const tableProps: TableProps<IOrder> = {
    columns,
    availableFilters,
    rowKey: "id",
    onSearchFilterSortPaginate,
    loading: isLoading || isFetching,
    data: data?.data,
    totalRows: data?.total_count,
    pagination,
    sort,
    filter,
    search,
  };

  const changeYear = () => {
    setPage(1);
  };

  const { data: orderYears, isLoading: orderYearsIsLoading } = useOrderStatusPerYear({
    orderType: "purchase",
  });

  const [isMoreOpen, setIsMoreOpen] = useState(false);

  return (
    <div>
      <PageHeader
        title="Purchase orders"
        description="Start with uploading all relevant purchase orders."
        right={
          <>
            <HelpButton
              size="small"
              type="link"
              url="https://help.root-sustainability.com/en/articles/9898425-purchase-orders"
            />
            <PageButtonGroup>
              <FileUploader
                uploadType={"po_orders"}
                uploadDataTableProps={{
                  columns: PURCHASE_ORDER_UPLOAD_COLUMNS,
                  rowKey: "id",
                  availableFilters,
                }}
                templateUrlFn={getPurchaseOrdersTemplate}
              />
              <FileDownloader
                fileOutputName="template-purchase-orders.xlsx"
                fileUrlFn={getPurchaseOrdersTemplate}
                buttonText="Template"
                icon={<DownloadOutlined />}
              />
            </PageButtonGroup>
          </>
        }
      />
      <div>
        <TableHeader
          left={
            <>
              <YearSelector
                onChange={changeYear}
                optionData={orderYears}
                isLoading={orderYearsIsLoading}
              />
              <PageButtonGroup loading={orderYearsIsLoading}>
                <ExportOrdersAction
                  orderType={"purchase"}
                  year={year}
                >
                  <Button icon={<ExportOutlined />}>Export</Button>
                </ExportOrdersAction>
                <MoreButton
                  onOpenChange={setIsMoreOpen}
                  open={isMoreOpen}
                  actions={
                    <DeleteOrdersAction
                      orderType={"purchase"}
                      year={year}
                      onSuccess={() => setIsMoreOpen(false)}
                    >
                      <ActionsPopOverItem
                        icon={<DeleteOutlined />}
                        danger
                      >
                        Delete
                      </ActionsPopOverItem>
                    </DeleteOrdersAction>
                  }
                />
              </PageButtonGroup>
            </>
          }
          right={
            <StatusTags
              data={data}
              loading={isLoading}
            />
          }
        />
        <Table {...tableProps} />
      </div>
    </div>
  );
}

export default PurchaseOrders;
